import React, { useEffect, useMemo } from 'react';

import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import PercentageFormField from '@components/form-fields/PercentageFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import TabPanel from '@components/layout/tabs/TabPanel';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { usePlanCoversLovs } from '@features/production/proposal/hooks';
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Tab, Tabs } from '@mui/material';
import { MAIN_THEME } from '@styles';

import { useGetGeoLocations } from '../amendment.slice';
import { FormValues, IPropertyRiskDetails } from '../types';

import { FieldArray, useFormikContext } from 'formik';
import { makeStyles } from 'tss-react/mui';

type Props = {
  status?: string;
  planId: string;
  currencySymbol: string;
};

type CoverDistributionRow = {
  planCoverID: string;
  riskRate: number | string;
  sumInsured: number | string;
  premium: number | string;
};

type PropertyRow = {
  riskDescription: string;
  geoLocationID: string;
  location: string;
  order?: number;
  planCoverPropertyDetails: CoverDistributionRow[];
};

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: 4,
    padding: '20px 12px 16px 18px',
    marginBottom: 14,
    position: 'relative',
    borderTopLeftRadius: 'unset'
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 23%)',
    gap: '2%',
    alignItems: 'center',
    marginBottom: 12
  },
  coverFieldRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 20%)',
    gap: '2%',
    alignItems: 'center',
    marginBottom: 10
  },
  addBtn: {
    marginLeft: '2px',
    minWidth: 'auto',
    minHeight: 'auto',
    backgroundColor: 'transparent',
    padding: 0,
    margin: '0 0 0 10px',
    alignSelf: 'center',
    border: 'none',
    cursor: 'pointer'
  },
  coverActionButtons: { display: 'flex', alignItems: 'center' },
  tabsContainer: {
    display: 'flex',
    alignItems: 'center',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    padding: 0,
    '&::-webkit-scrollbar': { height: '8px' },
    '&::-webkit-scrollbar-thumb': {
      background: MAIN_THEME.color.white86,
      borderRadius: 4
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: MAIN_THEME.color.white65
    }
  },
  tabsWrapper: { display: 'flex', alignItems: 'flex-end' },
  tabsRoot: {
    minHeight: 'auto',
    overflowX: 'auto',
    '& .MuiTabs-flexContainer': { borderBottom: 'none' }
  },
  indicator: { display: 'none' },
  tabRoot: {
    textTransform: 'none',
    minWidth: 72,
    minHeight: '31px',
    padding: '4px 8px',
    marginRight: 4,
    color: MAIN_THEME.color.black47,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderBottom: 'none',
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    backgroundColor: MAIN_THEME.color.white92,
    fontFamily: MAIN_THEME.font.regular,
    '&.Mui-selected': {
      color: MAIN_THEME.color.black,
      fontFamily: MAIN_THEME.font.medium,
      backgroundColor: MAIN_THEME.color.white98,
      borderBottom: 'none',
      fontWeight: 600
    }
  },
  tabLabel: {
    fontSize: MAIN_THEME.fontSize.sm,
    fontFamily: MAIN_THEME.font.regular,
    lineHeight: '18px',
    color: MAIN_THEME.color.black,
    display: 'flex',
    alignItems: 'center',
    padding: 0,
    backgroundColor: 'transparent'
  },
  tabIconButton: { marginLeft: 4, padding: 0 },
  closeIcon: { fontSize: MAIN_THEME.fontSize.rg },
  addUnderField: { display: 'flex', justifyContent: 'center', paddingTop: 8 },
  addIconBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer'
  }
}));

const PropertyRiskWidgetAtom: React.FC<Props> = ({
  status,
  planId,
  currencySymbol
}) => {
  const { classes } = useStyles();

  const locked =
    String(status ?? '').toUpperCase() === 'CONFIRMED' ||
    String(status ?? '').toUpperCase() === 'CANCELLED';

  const {
    values,
    setFieldValue,
    handleBlur,
    touched,
    errors,
    setFieldTouched
  } = useFormikContext<FormValues>();

  const rows = values.propertyRiskDetails;
  const [selectedTab, setSelectedTab] = React.useState(0);
  const handleTabChange = (_e: React.SyntheticEvent, v: number) =>
    setSelectedTab(v);

  const { data: geoData } = useGetGeoLocations();
  const { rawItems: planCovers } = usePlanCoversLovs(planId, [], 1);

  const policyCoverIds = useMemo(() => {
    return (values.covers ?? [])
      .map((c: any) =>
        String(
          c?.policyCover ??
            c?.planCoverID ??
            c?.policyPlanCoverParentID ??
            c?.coverID ??
            ''
        )
      )
      .filter(Boolean);
  }, [values.covers]);

  const coverLovOptions = useMemo<Record<string, string>>(() => {
    return (planCovers ?? [])
      .filter((pc: any) => {
        const id = pc?.id ?? pc?.planCoverId ?? pc?.planPlanCoverId ?? '';

        return policyCoverIds.length ? policyCoverIds.includes(id) : true;
      })
      .reduce((acc: Record<string, string>, pc: any) => {
        const id = pc?.id ?? pc?.planCoverId ?? pc?.planPlanCoverId ?? '';
        const code = pc?.cover?.code ?? '';
        const name = pc?.cover?.name ?? '';
        if (id && (code || name)) acc[id] = `${code} - ${name}`.trim();

        return acc;
      }, {});
  }, [planCovers, policyCoverIds]);

  const geoOptions = useMemo<Record<string, string>>(() => {
    const items = geoData?.production?.getGeoLocations ?? [];

    return items.reduce((acc: Record<string, string>, it: any) => {
      if (it?.id && it?.title) acc[it.id] = it.title;

      return acc;
    }, {});
  }, [geoData]);

  const computePremium = (riskRate: any, sumInsured: any) => {
    const r = Number(riskRate);
    const s = Number(sumInsured);
    if (!Number.isFinite(r) || !Number.isFinite(s)) return '';

    return Math.round(((r * s) / 100) * 1000) / 1000;
  };

  const shouldDisplayPlus = (row: IPropertyRiskDetails) => {
    const selectedIDs = row.planCoverPropertyDetails
      ?.map((c) => c.planCoverID)
      ?.filter(Boolean);
    const available = Object.keys(coverLovOptions).filter(
      (id) => !selectedIDs?.includes(id)
    );
    const firstFilled = Boolean(row.planCoverPropertyDetails?.[0]?.planCoverID);

    return !locked && available.length > 0 && firstFilled;
  };

  const getNextOrder = () => {
    if (!rows || rows.length === 0) return 1;
    const maxOrder = Math.max(...rows.map((r) => r.order || 0));

    return maxOrder + 1;
  };

  useEffect(() => {
    if (rows && rows.length > 0) {
      let needsUpdate = false;
      const updatedRows = rows.map((row, idx) => {
        if (row.order == null || row.order === 0) {
          needsUpdate = true;

          return { ...row, order: idx + 1 };
        }

        return row;
      });

      if (needsUpdate) {
        setFieldValue('propertyRiskDetails', updatedRows, false);
      }
    }
  }, []);

  const onCoverAdd = (idx: number) => {
    const curr =
      values.propertyRiskDetails?.[idx]?.planCoverPropertyDetails ?? [];

    setFieldValue(`propertyRiskDetails[${idx}].planCoverPropertyDetails`, [
      ...curr,
      {
        planCoverID: '',
        riskRate: '',
        sumInsured: '',
        premium: ''
      }
    ]);
  };

  const renderCoverRow = (
    row: IPropertyRiskDetails,
    pIdx: number,
    cover: any,
    cIdx: number,
    coversArray: any
  ) => {
    const selectedExceptCurrent = row.planCoverPropertyDetails
      ?.filter((_c, idx) => idx !== cIdx)
      .map((x) => x.planCoverID)
      .filter(Boolean);

    const availablePlanCovers: Record<string, string> = Object.fromEntries(
      Object.entries(coverLovOptions).filter(
        ([key]) => !selectedExceptCurrent?.includes(key)
      )
    );

    const coverTouched =
      (touched as any)?.propertyRiskDetails?.[pIdx]?.planCoverPropertyDetails?.[
        cIdx
      ] || {};
    const coverErrors =
      (errors as any)?.propertyRiskDetails?.[pIdx]?.planCoverPropertyDetails?.[
        cIdx
      ] || {};

    const handlePlanCoverChange = (v: string) => {
      setFieldValue(
        `propertyRiskDetails[${pIdx}].planCoverPropertyDetails[${cIdx}].planCoverID`,
        v,
        true
      );
    };

    const handleRiskRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setFieldValue(
        `propertyRiskDetails[${pIdx}].planCoverPropertyDetails[${cIdx}].riskRate`,
        val,
        true
      );

      const si = cover.sumInsured;
      if (val && si) {
        const computed = computePremium(val, si);
        if (computed !== '') {
          setFieldValue(
            `propertyRiskDetails[${pIdx}].planCoverPropertyDetails[${cIdx}].premium`,
            computed,
            false
          );
        }
      }
    };

    const handleSumInsuredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setFieldValue(
        `propertyRiskDetails[${pIdx}].planCoverPropertyDetails[${cIdx}].sumInsured`,
        val,
        true
      );

      const rr = cover.riskRate;
      if (val && rr) {
        const computed = computePremium(rr, val);
        if (computed !== '') {
          setFieldValue(
            `propertyRiskDetails[${pIdx}].planCoverPropertyDetails[${cIdx}].premium`,
            computed,
            false
          );
        }
      }
    };

    const handleRemoveCover = () => {
      coversArray.remove(cIdx);
    };

    return (
      <div key={`cover-${pIdx}-${cIdx}`} className={classes.coverFieldRow}>
        <SelectFormField
          name={`propertyRiskDetails[${pIdx}].planCoverPropertyDetails[${cIdx}].planCoverID`}
          title="Plan Cover*"
          placeholder="Plan Cover"
          value={cover.planCoverID}
          error={coverTouched.planCoverID ? coverErrors.planCoverID : ''}
          onBlur={() =>
            setFieldTouched(
              `propertyRiskDetails[${pIdx}].planCoverPropertyDetails[${cIdx}].planCoverID`,
              true,
              true
            )
          }
          onChange={handlePlanCoverChange}
          disabled={locked}
          selectOptions={availablePlanCovers}
          material
        />

        <PercentageFormField
          name={`propertyRiskDetails[${pIdx}].planCoverPropertyDetails[${cIdx}].riskRate`}
          title="Risk Rate*"
          placeholder="Risk Rate"
          value={cover.riskRate}
          error={coverTouched.riskRate ? coverErrors.riskRate : ''}
          onBlur={() =>
            setFieldTouched(
              `propertyRiskDetails[${pIdx}].planCoverPropertyDetails[${cIdx}].riskRate`,
              true,
              true
            )
          }
          onChange={handleRiskRateChange}
          disabled={locked}
          material
        />

        <CurrencyFormField
          name={`propertyRiskDetails[${pIdx}].planCoverPropertyDetails[${cIdx}].sumInsured`}
          title="Sum Insured*"
          placeholder="Sum Insured"
          value={cover.sumInsured}
          error={coverTouched.sumInsured ? coverErrors.sumInsured : ''}
          onBlur={() =>
            setFieldTouched(
              `propertyRiskDetails[${pIdx}].planCoverPropertyDetails[${cIdx}].sumInsured`,
              true,
              true
            )
          }
          onChange={handleSumInsuredChange}
          disabled={locked}
          material
          currencySymbol={currencySymbol}
        />

        <CurrencyFormField
          name={`propertyRiskDetails[${pIdx}].planCoverPropertyDetails[${cIdx}].premium`}
          title="Premium*"
          placeholder="Premium"
          value={cover.premium}
          error={coverTouched.premium ? coverErrors.premium : ''}
          onBlur={() =>
            setFieldTouched(
              `propertyRiskDetails[${pIdx}].planCoverPropertyDetails[${cIdx}].premium`,
              true,
              true
            )
          }
          onChange={(e) =>
            setFieldValue(
              `propertyRiskDetails[${pIdx}].planCoverPropertyDetails[${cIdx}].premium`,
              e.target.value,
              true
            )
          }
          disabled={locked}
          material
          currencySymbol={currencySymbol}
        />

        {!locked && (
          <div className={classes.coverActionButtons}>
            {row.planCoverPropertyDetails.length > 1 && (
              <IconButton
                type="button"
                onClick={handleRemoveCover}
                aria-label="Remove Cover"
                size="small"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderPropertyRow = (row: IPropertyRiskDetails, pIdx: number) => {
    const filteredGeoLocationLov = { ...geoOptions };
    (values.propertyRiskDetails || []).forEach((r, idx) => {
      if (idx !== pIdx && r.geoLocationID)
        delete filteredGeoLocationLov[r.geoLocationID];
    });

    const riskTouched = (touched as any)?.propertyRiskDetails?.[pIdx] || {};
    const riskErrors = (errors as any)?.propertyRiskDetails?.[pIdx] || {};

    return (
      <div className={classes.container}>
        <div className={classes.fieldRow}>
          {/* <NumberInputFormField
            name={`propertyRiskDetails[${pIdx}].order`}
            title="Order*"
            placeholder="Order"
            hidden={true}
            value={row.order || pIdx + 1}
            error={
              (touched?.propertyRiskDetails?.[pIdx] as any)?.order
                ? (errors?.propertyRiskDetails?.[pIdx] as any)?.order
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e: any) =>
              setFieldValue(
                `propertyRiskDetails[${pIdx}].order`,
                e.target.value,
                false
              )
            }
            disabled={true}
            material
          /> */}

          <TextInputFormField
            name={`propertyRiskDetails[${pIdx}].riskDescription`}
            title="Risk Description*"
            placeholder="Risk Description"
            value={row.riskDescription}
            onBlur={(e) => handleBlur(e)}
            onChange={(e: any) =>
              setFieldValue(
                `propertyRiskDetails[${pIdx}].riskDescription`,
                e.target.value,
                false
              )
            }
            error={
              (touched?.propertyRiskDetails?.[pIdx] as any)?.riskDescription
                ? (errors?.propertyRiskDetails?.[pIdx] as any)?.riskDescription
                : ''
            }
            disabled={locked}
            material
            fullWidth
          />

          <SelectFormField
            name={`propertyRiskDetails[${pIdx}].geoLocationID`}
            title="Geographical Location*"
            placeholder="Geographical Location"
            value={row.geoLocationID}
            onBlur={(_, e) => handleBlur(e)}
            onChange={(v: any) =>
              setFieldValue(
                `propertyRiskDetails[${pIdx}].geoLocationID`,
                v,
                false
              )
            }
            error={
              (touched?.propertyRiskDetails?.[pIdx] as any)?.geoLocationID
                ? (errors?.propertyRiskDetails?.[pIdx] as any)?.geoLocationID
                : ''
            }
            selectOptions={filteredGeoLocationLov}
            disabled={locked}
            material
            fullWidth
          />

          <TextInputFormField
            name={`propertyRiskDetails[${pIdx}].location`}
            title="Location"
            placeholder="Location"
            value={row.location}
            onBlur={(e) => handleBlur(e)}
            onChange={(e: any) =>
              setFieldValue(
                `propertyRiskDetails[${pIdx}].location`,
                e.target.value,
                false
              )
            }
            disabled={locked}
            material
            fullWidth
          />
        </div>

        <WidgetSection
          title="Cover Distribution"
          narrowSpacing
          hideHeaderBorder
        >
          <FieldArray
            name={`propertyRiskDetails[${pIdx}].planCoverPropertyDetails`}
            render={(coversArray) => (
              <>
                {(row.planCoverPropertyDetails?.length
                  ? row.planCoverPropertyDetails
                  : [
                      {
                        planCoverID: '',
                        riskRate: '',
                        sumInsured: '',
                        premium: ''
                      }
                    ]
                ).map((c, cIdx) =>
                  renderCoverRow(row, pIdx, c, cIdx, coversArray)
                )}

                {!locked && shouldDisplayPlus(row) && (
                  <div className={classes.addUnderField}>
                    <button
                      type="button"
                      className={classes.addIconBtn}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCoverAdd(pIdx);
                      }}
                      aria-label="Add Cover"
                    >
                      <AddCircleOutlineIcon fontSize="small" color="error" />
                    </button>
                  </div>
                )}
              </>
            )}
          />
        </WidgetSection>
      </div>
    );
  };

  const onPropertyAdd = (push: (v: PropertyRow) => void) => {
    const newOrder = getNextOrder();
    push({
      riskDescription: '',
      geoLocationID: '',
      location: '',
      order: newOrder,
      planCoverPropertyDetails: [
        {
          planCoverID: '',
          riskRate: '',
          sumInsured: '',
          premium: ''
        }
      ]
    });
    setSelectedTab(rows?.length ?? 0);
  };

  const onPropertyDelete = (idx: number) => {
    const currentRows = [...values.propertyRiskDetails];
    currentRows.splice(idx, 1);

    const reordered = currentRows.map((row, newIdx) => ({
      ...row,
      order: newIdx + 1
    }));

    setFieldValue('propertyRiskDetails', reordered, false);

    const nextLen = currentRows.length;
    if (selectedTab >= nextLen) setSelectedTab(Math.max(0, nextLen - 1));
  };

  return (
    <WidgetPaper>
      <WidgetSection title="Property Risk Details" useSeparator={false}>
        <FieldArray
          name={'propertyRiskDetails'}
          render={({ push, remove }) => (
            <>
              <div className={classes.tabsContainer}>
                <div className={classes.tabsWrapper}>
                  <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    aria-label="Property Details Tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    classes={{
                      root: classes.tabsRoot,
                      indicator: classes.indicator
                    }}
                  >
                    {(rows.length
                      ? rows
                      : [
                          {
                            riskDescription: '',
                            geographicalLocation: '',
                            location: '',
                            order: 1,
                            coverDistribution: [
                              {
                                coverId: '',
                                riskRate: '',
                                sumInsured: '',
                                premium: ''
                              }
                            ]
                          }
                        ]
                    ).map((_r, i) => (
                      <Tab
                        key={`property-tab-${i}`}
                        className={classes.tabRoot}
                        label={
                          <div className={classes.tabLabel}>
                            {`Risk ${i + 1}`}
                            {!locked && (rows?.length || 0) > 1 && (
                              <IconButton
                                type="button"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onPropertyDelete(i);
                                }}
                                aria-label="Remove Property Detail"
                                className={classes.tabIconButton}
                              >
                                <CloseIcon className={classes.closeIcon} />
                              </IconButton>
                            )}
                          </div>
                        }
                        id={`property-tab-${i}`}
                        aria-controls={`property-tabpanel-${i}`}
                      />
                    ))}
                  </Tabs>
                </div>
                {!locked && (
                  <IconButton
                    type="button"
                    className={classes.addBtn}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onPropertyAdd(push);
                    }}
                    disabled={locked}
                    aria-label="Add Property"
                  >
                    <AddIcon color="primary" />
                  </IconButton>
                )}
              </div>

              {(rows.length
                ? rows
                : [
                    {
                      riskDescription: '',
                      geoLocationID: '',
                      location: '',
                      order: 1,
                      planCoverPropertyDetails: [
                        {
                          planCoverID: '',
                          riskRate: 0,
                          sumInsured: '',
                          premium: ''
                        }
                      ]
                    }
                  ]
              ).map((row, i) =>
                selectedTab === i ? (
                  <TabPanel key={`property-panel-${i}`} index={i}>
                    {renderPropertyRow(row, i)}
                  </TabPanel>
                ) : null
              )}
            </>
          )}
        />
      </WidgetSection>
    </WidgetPaper>
  );
};

export { PropertyRiskWidgetAtom };
